import classNames from "classnames";
import { DropdownProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import { useCallback, useEffect, useRef, useState } from "react";
import "./style.scss";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { DropdownNode, NodeStates } from "../../build/types";
import arrowIcon from "../../assets/down-arrow.svg";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import DropdownOption from "./DropdownOption";

const BuilderDropdownField = ({
    node,
    builderProps,
    dropdownOptions,
    selectedOptionLabel,
    callback,
    disabled
}: DropdownProps) => {
    const dNode = node as DropdownNode;
    const { id, previewOptions, attr, input, optionWrapperStyle, stateManager } = dNode;
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const { setElement: setDropdownElement, states: dropdownStates } = useInteractionStates<HTMLLabelElement>();
    const { setElement: setInputElement, states: inputStates } = useInteractionStates<HTMLInputElement>();
    const { onDragStart, onDragEnd } = builderProps || {};
    const theOptions = dropdownOptions || previewOptions;

    useEffect(() => {
        if (builderProps?.active) {
            setDropdownOpen(true);
        } else {
            setDropdownOpen(false);
        }
    }, [builderProps?.active]);

    const dropdownRef = useRef<HTMLLabelElement | null>(null);
    const combinedRef = useCallback((el: HTMLLabelElement) => {
        setDropdownElement(el);
        if (dropdownRef) {
            dropdownRef.current = el;
        }
    }, [dropdownRef]);

    useOutsideClick(dropdownRef, () => {
        if (!builderProps) {    
            setDropdownOpen(false);
        }
    });

    const dropdownFieldClass = classNames("builder-dropdown-field", {
        [`${attr?.className}`]: !!attr?.className
    });

    const handleOptionClick = (value: string) => {
        if (value && callback) {
            callback(value);
        }
    };

    const inputStyle = input?.style;
    const labelStyle = input?.labelStyle;

    if (builderProps) {
        const { selectedMenu, nodeState, selectCallback, outlineCallback } = builderProps;
        return (
            <label
                {...attr}
                className={dropdownFieldClass}
                style={getCompoundStyle({
                    node,
                    otherStyle: labelStyle,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                id={id}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                ref={dropdownRef}
                draggable={onDragStart ? "true" : "false"}
                onDragStart={e => onDragStart?.(e, id)}
                onDragEnd={onDragEnd}
            >
                {input?.label}
                <div className="input-wrapper">
                    <input
                        type="text"
                        value={selectedOptionLabel || ""}
                        placeholder={input?.placeholder}
                        readOnly
                        disabled={true}
                        className={dropdownOpen ? "dropdown-open" : ""}
                        style={{...getCompoundStyle({
                            otherStyle: inputStyle,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "input_style")
                        }), pointerEvents: "none"}}
                    />
                    <img src={arrowIcon} className={dropdownOpen ? "invert-image" : ""} />
                    {dropdownOpen && (
                        <div
                            className="dropdown-field-option-wrapper"
                            style={getCompoundStyle({
                                otherStyle: optionWrapperStyle,
                                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "optionWrapperStyle")
                            })}
                        >
                            {theOptions &&
                                Object.entries(theOptions).map(([value, label], index) => (
                                    <DropdownOption
                                        node={node}
                                        key={index}
                                        label={label}
                                        value={value}
                                        builder
                                        nodeState={nodeState}
                                    />
                                ))}
                        </div>
                    )}
                </div>
                {selectedMenu}
            </label>
        );
    } else {
        const { style, ...rest } = attr || {};

        const labelStyles = getStylesFromInteractiveStates({
            states: dropdownStates,
            defaultStyle: getCompoundStyle({ node, otherStyle: labelStyle }),
            stateManager,
            paths: ["attr_style", "input_labelStyle"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const inputStyles = getStylesFromInteractiveStates({
            states: inputStates,
            defaultStyle: dNode.input?.style,
            stateManager,
            paths: ["input_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const optionWrapperStyles = getStylesFromInteractiveStates({
            states: dropdownStates,
            defaultStyle: dNode.optionWrapperStyle,
            stateManager,
            paths: ["optionWrapperStyle"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <label {...rest} style={labelStyles} className={dropdownFieldClass} ref={combinedRef} id={node.id}>
                {input?.label}
                <div className="input-wrapper">
                    <input
                        type="text"
                        ref={setInputElement}
                        style={inputStyles}
                        value={selectedOptionLabel || ""}
                        placeholder={input?.placeholder}
                        readOnly
                        className={classNames("state-dropdown-field-input", {
                            ["dropdown-open"]: dropdownOpen
                        })}
                        onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
                        disabled={disabled}
                    />
                    <img src={arrowIcon} className={dropdownOpen ? "invert-image" : ""} />
                    {dropdownOpen && (
                        <div className="dropdown-field-option-wrapper" style={optionWrapperStyles}>
                            {theOptions &&
                                Object.entries(theOptions).map(([value, label], index) => (
                                    <DropdownOption
                                        node={node}
                                        key={index}
                                        label={label}
                                        value={value}
                                        callback={() => handleOptionClick(value)}
                                    />
                                ))}
                        </div>
                    )}
                </div>
            </label>
        );
    }
};

export default BuilderDropdownField;
